import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid,
} from '@mui/material';
import { Close, Refresh } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from 'recharts';
import { apiService } from '../../services/api';

interface DriftTrajectoryModalProps {
  open: boolean;
  onClose: () => void;
}

interface TrajectoryDataPoint {
  day: number;
  date: string;
  dailyPlanned: number;
  cumulativePlanned: number;
  dailyActual: number;
  cumulativeActual: number;
  dailyVariance: number;
  cumulativeDrift: number;
  status: string;
  hasActuals: boolean;
}

interface ProjectionData {
  currentDay: number;
  totalScriptPages: number;
  totalScheduledDays: number;
  pagesShotToDate: number;
  pagesRemaining: number;
  currentVelocity: number;
  projectedDaysRemaining: number;
  projectedWrapDay: number;
  scheduledWrapDay: number;
  varianceDays: number;
  projectionStatus: string;
}

interface ProjectedFuturePoint {
  day: number;
  cumulativePages: number;
  isProjection: boolean;
}

interface TrajectoryResponse {
  trajectory: TrajectoryDataPoint[];
  projection: ProjectionData | null;
  projectedFuture: ProjectedFuturePoint[];
  metadata: {
    totalScriptPages: number;
    totalDays: number;
    currentDay: number;
    productionStartDate: string;
    scheduledWrapDate: string;
  };
}

const DriftTrajectoryModal: React.FC<DriftTrajectoryModalProps> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TrajectoryResponse | null>(null);

  useEffect(() => {
    if (open) {
      fetchTrajectoryData();
    }
  }, [open]);

  const fetchTrajectoryData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getDriftTrajectoryData();
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.error || 'Failed to load trajectory data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error fetching trajectory data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      // First trigger a server-side refresh of the summary data
      await apiService.refreshDriftTrajectory();
      // Then fetch the updated data
      await fetchTrajectoryData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error refreshing trajectory data');
    } finally {
      setRefreshing(false);
    }
  };

  // Calculate velocity first (needed for projection)
  const velocity = data?.projection?.currentVelocity || 0;
  const totalDays = data?.metadata?.totalDays || 25;

  // Prepare chart data - map from API response fields
  const chartData = data?.trajectory?.map((point) => ({
    day: point.day,
    date: point.date ? new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : `Day ${point.day}`,
    planned: point.cumulativePlanned,
    actual: point.hasActuals ? point.cumulativeActual : null,
    revised: null, // Will be populated when schedule revisions are tracked
    projected: null as number | null,
  })) || [];

  // Find the last day with actual data
  const lastActualDay = data?.trajectory?.filter(p => p.hasActuals).slice(-1)[0];
  const lastActualCumulative = lastActualDay?.cumulativeActual || 0;
  const lastActualDayNum = lastActualDay?.day || 0;

  // Calculate projected trajectory from last actual day through end of production
  // This creates the orange projection line based on current velocity
  if (lastActualDayNum > 0 && velocity > 0) {
    // Add projection starting from last actual day
    for (let day = lastActualDayNum; day <= totalDays; day++) {
      const daysFromLastActual = day - lastActualDayNum;
      const projectedCumulative = lastActualCumulative + (daysFromLastActual * velocity);
      
      const existingPoint = chartData.find(d => d.day === day);
      if (existingPoint) {
        existingPoint.projected = projectedCumulative;
      } else {
        // Add new day point if it doesn't exist in trajectory
        chartData.push({
          day: day,
          date: `Day ${day}`,
          planned: null as any,
          actual: null,
          revised: null,
          projected: projectedCumulative,
        });
      }
    }
    // Sort by day after adding projections
    chartData.sort((a, b) => a.day - b.day);
  }
  const projectedWrapDay = data?.projection?.projectedWrapDay || data?.metadata?.totalDays || 0;
  const plannedWrapDay = data?.projection?.scheduledWrapDay || data?.metadata?.totalDays || 0;
  const daysVariance = data?.projection?.varianceDays || 0;
  const currentDay = data?.projection?.currentDay || data?.metadata?.currentDay || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1A202C',
          minHeight: '70vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          📈 Production Velocity Chart
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton 
            onClick={handleRefresh} 
            size="small" 
            disabled={refreshing || loading}
            title="Refresh data"
            sx={{ 
              color: '#4299E1',
              '&:hover': { backgroundColor: 'rgba(66, 153, 225, 0.1)' }
            }}
          >
            <Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && data && (
          <>
            {/* Chart */}
            <Box sx={{ height: 400, mb: 3 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 40, right: 30, left: 20, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
                  <XAxis 
                    dataKey="day" 
                    stroke="#A0AEC0"
                    tick={{ fill: '#A0AEC0' }}
                  />
                  <YAxis 
                    stroke="#A0AEC0"
                    tick={{ fill: '#A0AEC0' }}
                    label={{ value: 'Cumulative Pages', angle: -90, position: 'insideLeft', fill: '#A0AEC0', offset: 10 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#2D3748',
                      border: '1px solid #4A5568',
                      borderRadius: 8,
                    }}
                    labelStyle={{ color: '#E2E8F0' }}
                    formatter={(value) => value != null ? [`${Number(value).toFixed(1)} pages`, ''] : ['—', '']}
                    labelFormatter={(label) => `Day ${label}`}
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    wrapperStyle={{ paddingBottom: '10px' }}
                  />
                  
                  {/* Original Plan Line */}
                  <Line
                    type="monotone"
                    dataKey="planned"
                    name="Original Plan"
                    stroke="#4299E1"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                  />
                  
                  {/* Actual Progress Line */}
                  <Line
                    type="monotone"
                    dataKey="actual"
                    name="Actual Progress"
                    stroke="#48BB78"
                    strokeWidth={3}
                    dot={{ fill: '#48BB78', r: 4 }}
                    connectNulls={false}
                  />
                  
                  {/* Projected Trajectory Line */}
                  <Line
                    type="monotone"
                    dataKey="projected"
                    name="Projected"
                    stroke="#ED8936"
                    strokeWidth={2}
                    strokeDasharray="3 3"
                    dot={false}
                    connectNulls={false}
                  />
                  
                  {/* Revised Schedule Line (if applicable) */}
                  <Line
                    type="monotone"
                    dataKey="revised"
                    name="Revised Schedule"
                    stroke="#9F7AEA"
                    strokeWidth={2}
                    dot={false}
                    connectNulls={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>

            {/* Metrics Summary */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#2D3748' }}>
                  <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                    {velocity.toFixed(1)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    pages/day velocity
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#2D3748' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: daysVariance <= 0 ? '#48BB78' : '#F56565' }}>
                    Day {projectedWrapDay}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Projected Wrap {daysVariance > 0 ? `(+${daysVariance} days)` : daysVariance < 0 ? `(${daysVariance} days)` : '(on target)'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#2D3748' }}>
                  <Typography variant="h5" color="secondary" sx={{ fontWeight: 'bold' }}>
                    Day {currentDay} / {plannedWrapDay}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Production Progress
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Legend Explanation */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#2D3748', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Legend</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6} sm={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 24, height: 3, backgroundColor: '#4299E1', borderStyle: 'dashed' }} />
                    <Typography variant="caption" color="text.secondary">Original Plan</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 24, height: 3, backgroundColor: '#48BB78' }} />
                    <Typography variant="caption" color="text.secondary">Actual Progress</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 24, height: 3, backgroundColor: '#ED8936', borderStyle: 'dotted' }} />
                    <Typography variant="caption" color="text.secondary">Projected</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 24, height: 3, backgroundColor: '#9F7AEA' }} />
                    <Typography variant="caption" color="text.secondary">Revised Schedule</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DriftTrajectoryModal;
